import { App } from '@capacitor/app';
import { CapacitorHttp } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { Network } from '@capacitor/network';
import { Preferences } from '@capacitor/preferences';

const LOGFLARE_URL =
  'https://api.logflare.app/logs/json?source=ad98e058-fcb2-4525-9c4b-783703a39cec';
const LOGFLARE_KEY = '0beDSXcwUAaU';

interface LogMetadata {
  level?: string;
  sim_number: string | null;
  imsi: string | null;
  device_id: string | null;
  session_id: string;
  app_version: string | null;
  build_number: string | null;
  platform: string | null;
  os_version: string | null;
  device_model: string | null;
  is_simulator: boolean | null;
  network_type: string | null;
  current_route: string | null;
  locale: string | null;
  timezone: string | null;
}

const metadata: LogMetadata = {
  sim_number: null,
  imsi: null,
  device_id: null,
  session_id: genSessionId(),
  app_version: null,
  build_number: null,
  platform: null,
  os_version: null,
  device_model: null,
  is_simulator: null,
  network_type: null,
  current_route: null,
  locale: null,
  timezone: null,
};

let origLog: typeof console.log = console.log.bind(console);
let origWarn: typeof console.warn = console.warn.bind(console);
let origError: typeof console.error = console.error.bind(console);
let initialized = false;

function genSessionId(): string {
  const c: any = (globalThis as any).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  // Fallback: RFC4122-ish v4 from Math.random
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function stringifyArg(a: any): string {
  if (typeof a === 'string') return a;
  try {
    return JSON.stringify(a);
  } catch {
    return String(a);
  }
}

function sendToLogflare(level: string, args: any[]): void {
  const message = args.map(stringifyArg).join(' ');
  const payload = {
    batch: [
      {
        message,
        metadata: { ...metadata, level },
      },
    ],
  };
  try {
    CapacitorHttp.request({
      method: 'POST',
      url: LOGFLARE_URL,
      headers: { 'Content-Type': 'application/json', 'X-API-KEY': LOGFLARE_KEY },
      data: payload,
    }).catch(() => {
      // Swallow — logging failures must not break the app.
    });
  } catch {
    // Swallow synchronous throws too.
  }
}

async function hydrateDeviceInfo(): Promise<void> {
  try {
    const info = await Device.getInfo();
    metadata.platform = info.platform ?? null;
    metadata.os_version = info.osVersion ?? null;
    metadata.device_model = info.model ?? null;
    metadata.is_simulator = typeof info.isVirtual === 'boolean' ? info.isVirtual : null;
  } catch (e) {
    origError('logger: Device.getInfo failed', e);
  }
  try {
    const id = await Device.getId();
    metadata.device_id = (id as any).identifier ?? (id as any).uuid ?? null;
  } catch (e) {
    origError('logger: Device.getId failed', e);
  }
}

async function hydrateAppInfo(): Promise<void> {
  try {
    const info = await App.getInfo();
    metadata.app_version = info.version ?? null;
    metadata.build_number = info.build ?? null;
  } catch (e) {
    // On web App.getInfo is unimplemented — not an error worth logging loudly.
    origLog('logger: App.getInfo unavailable');
  }
}

async function hydrateNetwork(): Promise<void> {
  try {
    const status = await Network.getStatus();
    metadata.network_type = status.connectionType ?? null;
  } catch (e) {
    origError('logger: Network.getStatus failed', e);
  }
}

export async function refreshSimIdentity(): Promise<void> {
  try {
    const sim = await Preferences.get({ key: 'sim-number' });
    const imsi = await Preferences.get({ key: 'random-imsi-number' });
    metadata.sim_number = sim.value ?? null;
    metadata.imsi = imsi.value ?? null;
  } catch (e) {
    origError('logger: refreshSimIdentity failed', e);
  }
}

export function setCurrentRoute(path: string | null): void {
  metadata.current_route = path;
}

export async function initLogger(): Promise<void> {
  if (initialized) return;
  initialized = true;

  // Capture originals BEFORE overriding so the logger can emit without recursion.
  origLog = console.log.bind(console);
  origWarn = console.warn.bind(console);
  origError = console.error.bind(console);

  // Synchronous fields available immediately.
  try {
    const opts = Intl.DateTimeFormat().resolvedOptions();
    metadata.locale = opts.locale ?? null;
    metadata.timezone = opts.timeZone ?? null;
  } catch {
    // Leave as null.
  }

  // Install overrides now — early logs go through with partial metadata.
  console.log = (...args: any[]) => {
    sendToLogflare('log', args);
    origLog(...args);
  };
  console.warn = (...args: any[]) => {
    sendToLogflare('warn', args);
    origWarn(...args);
  };
  console.error = (...args: any[]) => {
    sendToLogflare('error', args);
    origError(...args);
  };

  // Kick off async hydration in parallel.
  await Promise.all([
    hydrateDeviceInfo(),
    hydrateAppInfo(),
    hydrateNetwork(),
    refreshSimIdentity(),
  ]);

  // Track network changes so `network_type` stays current.
  try {
    Network.addListener('networkStatusChange', (status) => {
      metadata.network_type = status.connectionType ?? null;
    });
  } catch (e) {
    origError('logger: Network.addListener failed', e);
  }

  // Confirm the pipeline with an enriched init log.
  console.log('LOGFLARE_INIT: app started at ' + new Date().toISOString());
}
